import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  UserDocument,
  ReadingAssignment,
  DocumentListParams,
  ReadingListParams,
} from '@/types';

export function useMyDocuments(params?: DocumentListParams) {
  return useQuery({
    queryKey: ['documents', 'list', params],
    queryFn: () =>
      api.get<UserDocument[]>('/documents', {
        page: params?.page ? String(params.page) : undefined,
        per_page: params?.per_page ? String(params.per_page) : undefined,
        category: params?.category,
        search: params?.search,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useMyReadings(params?: ReadingListParams) {
  return useQuery({
    queryKey: ['readings', 'list', params],
    queryFn: () =>
      api.get<ReadingAssignment[]>('/documents/readings/mine', {
        page: params?.page ? String(params.page) : undefined,
        per_page: params?.per_page ? String(params.per_page) : undefined,
        status: params?.status,
        search: params?.search,
      }),
    placeholderData: keepPreviousData,
  });
}

// #28/#23/#30 — URL inline para visualizar el documento (PDF/imagen/Office→PDF) sin
// descargarlo ni abrir una URL almacenada vencida. Se dispara solo al abrir el visor.
export type DocumentViewInfo = { url: string; kind: 'pdf' | 'image' | 'external' | 'download' };

export function useDocumentViewUrl(documentId: number | null) {
  return useQuery({
    queryKey: ['documents', 'view', documentId],
    queryFn: () => api.get<DocumentViewInfo>(`/documents/${documentId}/view`),
    select: (res) => res.data,
    enabled: documentId !== null,
    staleTime: 30_000,
  });
}

export function useConfirmReading() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: number) => api.post(`/documents/${documentId}/readings/confirm`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['readings'] });
    },
  });
}
